import { useSession } from "@/contexts/SessionContext";
import { ShieldOff, Loader2, PlugZap, RefreshCw } from "lucide-react";
import LoginPage from "@/pages/LoginPage";
import { Button } from "@/components/ui/button";
import { asText } from "@/lib/format";

/**
 * Renders one of these states:
 *  - loading: pre-fetch shimmer
 *  - unauthenticated: render local LoginPage (1OS Bills login)
 *  - dependency_unavailable: Control Room could not be reached — fail closed, offer one retry
 *  - denied: shows access denied / capability message
 *  - authed: renders children
 *
 * Launcher token handoff (?token= / #token=) is captured in SessionProvider
 * BEFORE this gate renders, so users who arrive from the Control Room
 * launcher never see the login page.
 */
export default function AuthGate({ children }) {
  const { loading, authStatus, errorPayload, me, refresh, signOut } = useSession();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-archival-base">
        <div className="flex items-center gap-2 text-archival-secondary text-[13px]">
          <Loader2 className="animate-spin" size={16} /> Loading 1OS Bills…
        </div>
      </div>
    );
  }

  if (authStatus === "unauthenticated") {
    return <LoginPage />;
  }

  if (authStatus === "dependency_unavailable") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-archival-base p-6">
        <div
          data-testid="identity-unavailable-card"
          className="max-w-md w-full rounded-md border border-amber-200 bg-amber-50 p-8 text-center"
        >
          <PlugZap size={28} className="text-amber-700 mx-auto mb-3" />
          <h1 className="text-[22px] font-semibold text-amber-900 mb-2">Sign-in service unavailable</h1>
          <p className="text-[13px] text-amber-800">
            1OS Bills could not reach 1OS Control Room to confirm your session, so nothing has been loaded.
            Your sign-in has not been lost — try again in a few moments.
          </p>
          <Button
            data-testid="identity-unavailable-retry"
            onClick={() => refresh()}
            className="mt-5 text-white"
            style={{ backgroundColor: "var(--action)" }}
          >
            <RefreshCw size={14} className="mr-2" /> Try again
          </Button>
        </div>
      </div>
    );
  }

  if (authStatus === "denied") {
    const payload = errorPayload || {};
    const code = asText(payload.error_code);
    const failedCheck = asText(payload.failed_check);
    // Control Room distinguishes an ORGANISATION entitlement verdict from a user-permission one. Never
    // describe an organisation-level denial as a missing user capability.
    const orgLevel = code === "ORG_NOT_ENTITLED" || failedCheck === "ORGANISATION_ENTITLEMENT";
    const orgName = asText(payload.org_name) || asText(payload.org_id) ||
      asText(payload.organisation_id) || asText(payload.organization_id);
    return (
      <div className="min-h-screen flex items-center justify-center bg-archival-base p-6">
        <div
          data-testid="access-denied-card"
          className="max-w-md w-full rounded-md border border-red-200 bg-red-50 p-8 text-center"
        >
          <ShieldOff size={28} className="text-red-700 mx-auto mb-3" />
          <h1 className="text-[22px] font-semibold text-red-900 mb-2">
            {orgLevel ? "Organisation not entitled" : "Access denied"}
          </h1>
          <p className="text-[13px] text-red-800 mb-1" data-testid="access-denied-message">
            {orgLevel
              ? "Control Room has not enabled 1OS Bills for this organisation."
              : asText(payload.message) || "You do not have access to 1OS Bills."}
          </p>
          <p className="text-[12px] text-red-800" data-testid="access-denied-explanation">
            {orgLevel
              ? `This is an organisation entitlement, not a password problem${
                  orgName ? ` (${orgName})` : ""
                }. Ask your administrator to enable 1OS Bills for the organisation in Control Room, then sign in again.`
              : "Your sign-in worked. 1OS Control Room has not granted this account access to this app, so there is nothing to show. Ask your administrator to grant it."}
          </p>
          {orgLevel && asText(payload.message) && (
            <p className="text-[12px] text-red-700 mt-2" data-testid="access-denied-upstream">
              Control Room: {asText(payload.message)}
            </p>
          )}
          {orgLevel ? (
            <p className="text-[11px] font-mono text-red-700 mt-3" data-testid="access-denied-failed-check">
              Failed check: {failedCheck || "ORGANISATION_ENTITLEMENT"}
              {orgName ? ` · ${orgName}` : ""}
            </p>
          ) : (
            (payload.required_capability || payload.required) && (
              <p className="text-[11px] font-mono text-red-700 mt-3" data-testid="access-denied-capability">
                Required capability: {asText(payload.required_capability || payload.required)}
              </p>
            )
          )}
          {payload.app_code && (
            <p className="text-[11px] font-mono text-red-700" data-testid="access-denied-app">
              App: {asText(payload.app_code)}
              {payload.module_key ? ` · module ${asText(payload.module_key)}` : ""}
            </p>
          )}
          {code && (
            <p className="text-[11px] font-mono text-red-600 mt-1" data-testid="access-denied-code">
              {code}
            </p>
          )}
          <p className="text-[12px] text-red-700 mt-4">
            {me?.user?.email ? `Signed in as ${me.user.email}.` : "Contact your administrator to request access."}
          </p>
          <Button
            data-testid="access-denied-switch-account"
            variant="outline"
            onClick={() => signOut()}
            className="mt-4 border-red-300 text-red-800"
          >
            Sign in with a different account
          </Button>
        </div>
      </div>
    );
  }

  return children;
}
